// src/features/media/components/ClipRangeSelector.tsx

import { Stack, Group, Text, RangeSlider, Button, Tooltip } from '@mantine/core';
import { MediaUtils } from '../MediaUtils';
import { TimecodeInput } from './TimecodeInput';

interface ClipRangeSelectorProps {
  duration: number; // Total video duration in seconds
  startSecond: number; // Current in point
  endSecond: number; // Current out point
  playerTime: number | null; // Preview player's reported time; null until it reports
  onChange: (start: number, end: number) => void;
  onSetIn?: () => void; // Set in point at current player time
  onSetOut?: () => void; // Set out point at current player time
}

interface SetPointButtonProps {
  playerTime: number | null;
  onClick: () => void;
}

/**
 * "Set" capture button for one clip point. Inactive until the preview player
 * has reported a time — capturing before that would commit the 0:00
 * placeholder over the user's selection. Uses data-disabled (not disabled) so
 * the explanatory tooltip still receives mouse events (Mantine pattern).
 */
function SetPointButton({ playerTime, onClick }: SetPointButtonProps) {
  const inactive = playerTime === null;
  return (
    <Tooltip
      label={
        inactive
          ? 'Play or scrub the preview first'
          : `Set to the player's current time (${MediaUtils.formatTimecode(playerTime)})`
      }
      withArrow
    >
      <Button
        variant="light"
        size="compact-xs"
        data-disabled={inactive || undefined}
        aria-disabled={inactive}
        onClick={(e) => {
          if (inactive) {
            e.preventDefault();
            return;
          }
          onClick();
        }}
      >
        Set
      </Button>
    </Tooltip>
  );
}

/**
 * Clip range selection: editable In/Out timecode fields for precision,
 * timeline slider for coarse selection and visual context.
 */
export function ClipRangeSelector({
  duration,
  startSecond,
  endSecond,
  playerTime,
  onChange,
  onSetIn,
  onSetOut,
}: ClipRangeSelectorProps) {
  const clipDuration = endSecond - startSecond;

  return (
    <Stack gap="xs">
      {/* Editable time fields with Set buttons */}
      <Group justify="space-between">
        <Group gap="xs">
          <Text size="sm" fw={500}>
            In:
          </Text>
          <TimecodeInput
            value={startSecond}
            min={0}
            max={endSecond - 1}
            ariaLabel="Clip in point"
            onCommit={(seconds) => onChange(seconds, endSecond)}
          />
          {onSetIn && <SetPointButton playerTime={playerTime} onClick={onSetIn} />}
        </Group>
        <Group gap="xs">
          {onSetOut && <SetPointButton playerTime={playerTime} onClick={onSetOut} />}
          <Text size="sm" fw={500}>
            Out:
          </Text>
          <TimecodeInput
            value={endSecond}
            min={startSecond + 1}
            max={duration}
            ariaLabel="Clip out point"
            onCommit={(seconds) => onChange(startSecond, seconds)}
          />
        </Group>
      </Group>

      {/* Range slider */}
      <RangeSlider
        min={0}
        max={duration}
        step={1}
        minRange={1}
        value={[startSecond, endSecond]}
        onChange={([start, end]) => onChange(start, end)}
        label={(value) => MediaUtils.formatTimecode(value)}
        labelAlwaysOn={false}
        marks={[
          { value: 0, label: MediaUtils.formatTimecode(0) },
          { value: duration, label: MediaUtils.formatTimecode(duration) },
        ]}
        styles={{
          markLabel: { fontSize: 'var(--mantine-font-size-xs)' },
        }}
        mb="lg"
      />

      {/* Duration display */}
      <Text size="sm" fw={500}>
        Clip Duration: {MediaUtils.formatTimecode(clipDuration)}
      </Text>
    </Stack>
  );
}
